import { identity } from 'fp-ts/function';
import { useFormContext } from 'react-hook-form';
import { SearchType, TextKey } from '@mews-commander/enums';
import { mapAzureLocation } from '@mews-commander/utils';
import { FormInputField, LocalizedText } from '@mews/framework';
import { Size } from '@optimus-web/common';
import Search from '../../../modules/search/components/Search';
import { CountrySelectField } from './CountrySelectField';

export enum AddressFieldName {
    addressLine1 = 'addressLine1',
    addressLine2 = 'addressLine2',
    city = 'city',
    countryCode = 'countryCode',
    countrySubdivisionCode = 'countrySubdivisionCode',
    postalCode = 'postalCode',
}

interface AddressFormFieldsProps {
    autocompleteLabel?: Mews.TextKey;
    transformFieldName?: (fieldName: AddressFieldName) => string;
    transformDataTestId?: (dataTestId: string) => string;
    required?: Partial<Record<AddressFieldName, boolean>>;
    disabled?: Partial<Record<AddressFieldName, boolean>>;
    addressSearchAutocomplete?: boolean;
    size?: Size;
}

export const AddressFormFields = ({
    autocompleteLabel = TextKey.AdressAutocomplete,
    disabled,
    required,
    size,
    transformDataTestId = identity,
    transformFieldName = identity,
    ...other
}: AddressFormFieldsProps) => {
    const { setValue } = useFormContext();

    const addressLine1 = transformFieldName(AddressFieldName.addressLine1);
    const addressLine2 = transformFieldName(AddressFieldName.addressLine2);
    const city = transformFieldName(AddressFieldName.city);
    const postalCode = transformFieldName(AddressFieldName.postalCode);
    const countryCode = transformFieldName(AddressFieldName.countryCode);
    const countrySubdivisionCode = transformFieldName(AddressFieldName.countrySubdivisionCode);

    const shouldDirty = { shouldDirty: true };
    return (
        <>
            {other.addressSearchAutocomplete ? (
                <Search
                    name="addressAutocomplete"
                    label={<LocalizedText textKey={autocompleteLabel} />}
                    searchTypes={[SearchType.Locations]}
                    // @ts-expect-error $TSFixMeError: Property 'options' does not exist on type 'SearchResult'.
                    onResultClick={({ options }) => {
                        const address = mapAzureLocation(options);
                        setValue(addressLine1, address.addressLine1 ?? '', shouldDirty);
                        setValue(addressLine2, address.addressLine2 ?? '', shouldDirty);
                        setValue(city, address.city ?? '', shouldDirty);
                        setValue(postalCode, address.postalCode ?? '', shouldDirty);
                        setValue(countryCode, address.countryCode ?? '', shouldDirty);
                        setValue(countrySubdivisionCode, '', shouldDirty);
                    }}
                    scope="addressAutocomplete"
                    size={size}
                    showInvoiceableTag={false}
                    data-test-id={transformDataTestId('addressAutocomplete')}
                />
            ) : null}
            <FormInputField
                name={addressLine1}
                label={<LocalizedText textKey={TextKey.AddressLine1} />}
                required={required?.addressLine1}
                disabled={disabled?.addressLine1}
                size={size}
            />
            <FormInputField
                name={addressLine2}
                label={<LocalizedText textKey={TextKey.AddressLine2} />}
                required={required?.addressLine2}
                disabled={disabled?.addressLine2}
                size={size}
            />
            <FormInputField
                name={city}
                label={<LocalizedText textKey={TextKey.City} />}
                required={required?.city}
                disabled={disabled?.city}
                size={size}
            />
            <FormInputField
                name={postalCode}
                label={<LocalizedText textKey={TextKey.PostalCode} />}
                required={required?.postalCode}
                disabled={disabled?.postalCode}
                size={size}
            />
            <CountrySelectField
                countryFieldName={countryCode}
                countrySubdivisionFieldName={countrySubdivisionCode}
                required={{
                    [countryCode]: required?.countryCode,
                    [countrySubdivisionCode]: required?.countrySubdivisionCode,
                }}
                size={size}
                disabled={{
                    [countryCode]: disabled?.countryCode,
                    [countrySubdivisionCode]: disabled?.countrySubdivisionCode,
                }}
            />
        </>
    );
};
